import { useState, useEffect, useRef } from 'react';
import { useQuery } from './QueryContext';
import styles from './Music.module.css';
import Header from './components/Header';
import SideBar from './components/SideBar';
import QueryPlatform from './components/QueryPlatform';
import QueryContainer from './components/QueryContainer';
import ListTitle from './components/ListTitle';
import ListHeader from './components/ListHeader';
import { downloadJson, sanitizeFilenamePart } from './utils/downloadJson';
import { downloadBlob } from './utils/downloadFile';
import { toCsv } from './utils/toCsv';

const proxyUrl = (url) => {
  if (!url) return '';
  if (/scontent[^/]*\.cdninstagram\.com|\.fbcdn\.net|instagram\.com\/reels\/audio\//.test(url)) {
    return `/api/proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const proxyImg = proxyUrl;

const getCountryFlag = (countryCode) => {
  if (!countryCode) return '';
  return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
};

// 화면에서 인앱 재생이 불가한 Instagram/Meta CDN URL을 식별 (모든 instagram.com URL 포함)
const isInstagramAudioUrl = (url) =>
  !!url && /instagram\.com|cdninstagram\.com|fbcdn\.net/.test(url);

const mapMusicItem = (item, platform, selectedCountry) => ({
  rank: item.rank,
  song: item.song || item.data?.song || '',
  artist: item.artist || item.data?.artist || '',
  country: platform === 'youtube' ? selectedCountry : (item.country || ''),
  no_of_used: item.no_of_used ?? null,
  album_image: item.albumImg || '',
  play_url: isInstagramAudioUrl(item.playUrl) ? null : (item.playUrl || null),
  related_videos: (item.related_videos || []).map((v) => ({
    url: v.url || '',
    thumbnail: v.thumbnail || '',
  })),
});

const MusicRankItem = ({ rank, song, artist, albumImg, country, noOfUsed, videos, playUrl, linkUrl, isPlaying, onPlay }) => {
  return (
    <div className={styles.rankRow}>
      <div className={styles.rankCell}>
        <span className={styles.rankNum}>{rank}</span>
      </div>
      <div className={styles.songCell}>
        {linkUrl ? (
          <span
            className={styles.playBtn}
            onClick={() => window.open(linkUrl, '_blank', 'noopener,noreferrer')}
            title="Open"
          >▶</span>
        ) : playUrl && (
          <span
            className={`${styles.playBtn} ${isPlaying ? styles.playBtnActive : ''}`}
            onClick={() => onPlay(playUrl)}
            title={isPlaying ? 'Pause' : 'Play'}
          >{isPlaying ? '⏸' : '▶'}</span>
        )}
        {song}
      </div>
      <div className={styles.artistCell}>{artist}</div>
      <div className={styles.albumCell}>
        {albumImg
          ? <img src={proxyImg(albumImg)} alt="" className={styles.albumImg} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; }} />
          : <div className={styles.albumPlaceholder} />}
      </div>
      <div className={styles.countryCell}>
        {country && <img src={getCountryFlag(country)} alt={country} className={styles.flag} />}
      </div>
      <div className={styles.noOfUsedCell}>{noOfUsed || '+000 M'}</div>
      <div className={styles.relatedCell}>
        {(videos || []).slice(0, 5).map((video, i) => (
          <div
            key={i}
            className={styles.videoThumb}
            onClick={() => video.url && window.open(video.url, '_blank', 'noopener,noreferrer')}
            style={{ cursor: video.url ? 'pointer' : 'default' }}
          >
            {video.thumbnail && (
              <img src={proxyImg(video.thumbnail)} alt="" className={styles.thumbImg} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            )}
          </div>
        ))}
      </div>
      <div className={styles.rowDivider} />
    </div>
  );
};

const Music = () => {
  const [musicList, setMusicList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingUrl, setPlayingUrl] = useState(null);
  const [dateRange, setDateRange] = useState({ minDate: null, maxDate: null });
  const { selectedPlatform, setSelectedPlatform, selectedCountry, selectedCategory, selectedSubCategory, selectedDate, setSelectedDate } = useQuery();
  const audioRef = useRef(null);

  useEffect(() => {
    if (!selectedPlatform || !selectedCountry || !selectedCategory) return;
    let cancelled = false;
    fetch(`/api/dates?platform=${encodeURIComponent(selectedPlatform)}&country=${encodeURIComponent(selectedCountry)}&category=${encodeURIComponent(selectedCategory)}&subcategory=${encodeURIComponent(selectedSubCategory)}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled || !d.success) return;
        setDateRange({
          minDate: d.data.minDate ? new Date(d.data.minDate) : null,
          maxDate: d.data.maxDate ? new Date(d.data.maxDate) : null,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedPlatform, selectedCountry, selectedCategory, selectedSubCategory]);

  const handlePlay = (url) => {
    const proxied = proxyUrl(url);
    if (playingUrl === url) {
      // 같은 곡: 재생/일시정지 토글
      if (audioRef.current.paused) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
        setPlayingUrl(null);
      }
    } else {
      // 다른 곡: 교체 재생
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(proxied);
      audioRef.current.play();
      audioRef.current.onended = () => setPlayingUrl(null);
      setPlayingUrl(url);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchMusicList = async () => {
      if (!selectedPlatform || !selectedCountry || !selectedCategory) {
        if (cancelled) return;
        setMusicList([]);
        setLoading(false);
        return;
      }
      // 페이지 내용 변경 시 재생 중인 음악 중단
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingUrl(null);
      try {
        setLoading(true);
        setError(null);

        const dateParam = selectedDate
          ? `&date=${selectedDate.toISOString().slice(0, 10)}`
          : '';
        const response = await fetch(
          `/api/music?platform=${encodeURIComponent(selectedPlatform)}&category=${encodeURIComponent(selectedCategory)}&subcategory=${encodeURIComponent(selectedSubCategory)}&country=${encodeURIComponent(selectedCountry)}${dateParam}`
        );

        if (cancelled) return;
        if (!response.ok) throw new Error('Failed to fetch music list');

        const data = await response.json();
        if (cancelled) return;
        if (data.success) {
          const ranked = (data.data.items || [])
            .sort((a, b) => (b.no_of_used || 0) - (a.no_of_used || 0))
            .map((item, i) => ({ ...item, rank: i + 1 }));
          setMusicList(ranked);
          if (data.data.date) {
            const apiDate = new Date(data.data.date).toISOString().slice(0, 10);
            const curDate = selectedDate ? selectedDate.toISOString().slice(0, 10) : null;
            if (apiDate !== curDate) setSelectedDate(new Date(data.data.date));
          }
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setMusicList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMusicList();
    return () => { cancelled = true; };
  }, [selectedPlatform, selectedCountry, selectedCategory, selectedSubCategory, selectedDate]);

  // 페이지 이탈 시 재생 중인 음악 중단
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleDownload = (format) => {
    const items = musicList.map((item) =>
      mapMusicItem(item, selectedPlatform, selectedCountry)
    );
    const dateStr = selectedDate
      ? selectedDate.toISOString().slice(0, 10)
      : 'unknown';
    const payload = {
      page: 'music',
      platform: selectedPlatform,
      category: selectedCategory,
      country: selectedCountry,
      date: dateStr,
      exported_at: new Date().toISOString(),
      count: items.length,
      items,
    };
    const baseName = [
      'music',
      sanitizeFilenamePart(selectedPlatform),
      sanitizeFilenamePart(selectedCategory),
      sanitizeFilenamePart(selectedCountry),
      sanitizeFilenamePart(dateStr),
    ].join('-');
    if (format === 'csv') {
      downloadBlob(toCsv(payload.items), baseName + '.csv', 'text/csv;charset=utf-8');
    } else {
      downloadJson(payload, baseName + '.json');
    }
  };

  return (
    <div className={styles.page}>
      <Header />
      <div className={styles.mainContent}>
        <SideBar />
        <div className={styles.body}>
          <QueryPlatform onPlatformChange={setSelectedPlatform} />
          <QueryContainer />

          <ListTitle
            title="Top 100 Short Form Video Music"
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            minDate={dateRange.minDate}
            maxDate={dateRange.maxDate}
            onDownload={handleDownload}
            disabled={loading || musicList.length === 0}
          />

          <ListHeader columns={[
            { label: 'Rank', className: styles.rankCell },
            { label: 'Song', className: styles.songCell },
            { label: 'Artist', className: styles.artistCell },
            { label: 'Album', className: styles.albumCell },
            { label: 'Country', className: styles.countryCell },
            { label: 'No. of used', className: `${styles.noOfUsedCell} ${styles.highlight}` },
            { label: 'Related Video', className: styles.relatedCell },
          ]} />

          {/* 리스트 */}
          {loading ? (
            <div className={styles.status}>Loading...</div>
          ) : error ? (
            <div className={styles.status}>No data available.</div>
          ) : musicList.length === 0 ? (
            <div className={styles.status}>No data available.</div>
          ) : (
            musicList.map((item, i) => (
              <MusicRankItem
                key={i}
                rank={item.rank}
                song={item.song || item.data?.song || 'Unknown'}
                artist={item.artist || item.data?.artist || 'Unknown'}
                albumImg={item.albumImg || ''}
                country={selectedPlatform === 'youtube' ? selectedCountry : (item.country || '')}
                noOfUsed={item.no_of_used}
                videos={item.related_videos}
                playUrl={item.playUrl && /instagram\.com|cdninstagram\.com|fbcdn\.net/.test(item.playUrl) ? null : item.playUrl}
                linkUrl={item.playUrl && /instagram\.com|cdninstagram\.com|fbcdn\.net/.test(item.playUrl) ? item.playUrl : null}
                isPlaying={playingUrl === item.playUrl}
                onPlay={handlePlay}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Music;
